/**
 * SetupManager — Administrative setup and data management zone.
 */
import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import api from '../services/api';

function SetupManager({ companyId }) {
  // ── Nuke state ─────────────────────────────────────────────────
  const [showNukeModal,  setShowNukeModal]  = useState(false);
  const [nukeConfirm,   setNukeConfirm]    = useState('');
  const [nuking,        setNuking]          = useState(false);
  const [nukeError,     setNukeError]       = useState('');
  const [nukeSuccess,   setNukeSuccess]     = useState(false);
  const [seedingSynthetic, setSeedingSynthetic] = useState(false);
  const [seedError, setSeedError] = useState('');
  const [seedResult, setSeedResult] = useState(null);

  const openNukeModal = () => {
    setNukeConfirm('');
    setNukeError('');
    setNukeSuccess(false);
    setShowNukeModal(true);
  };

  const handleNuke = async () => {
    if (nukeConfirm !== 'BORRAR') return;
    setNuking(true);
    setNukeError('');
    try {
      await api.nukeCompanyData(companyId);
      setNukeSuccess(true);
      setShowNukeModal(false);
    } catch (e) {
      setNukeError(e.message || 'Error al borrar datos');
    } finally {
      setNuking(false);
    }
  };

  const handleSeedSynthetic = async () => {
    if (!companyId) return;
    const confirmed = window.confirm(
      'Se poblaran datos sinteticos de demo en la empresa seleccionada. La operacion reutiliza el mismo pack demo si ya existe. ¿Continuar?'
    );
    if (!confirmed) return;

    setSeedingSynthetic(true);
    setSeedError('');
    try {
      const result = await api.seedSyntheticCompanyData(companyId);
      setSeedResult(result);
      setNukeSuccess(false);
    } catch (e) {
      setSeedError(e.message || 'Error al poblar datos sinteticos');
    } finally {
      setSeedingSynthetic(false);
    }
  };

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 900 }}>
      {/* ── Header ── */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <span style={{ fontSize: 32 }}>⚙️</span>
        <div>
          <h3 className="mb-0 fw-bold">Setup &amp; Mantenimiento</h3>
          <p className="text-muted mb-0 small">
            Operaciones administrativas sobre los datos de la empresa
          </p>
        </div>
      </div>

      {/* ── Success banner ── */}
      {nukeSuccess && (
        <div className="alert alert-success alert-dismissible d-flex align-items-center gap-2 mb-4">
          <span style={{ fontSize: 20 }}>✅</span>
          <div>
            <strong>Datos eliminados correctamente.</strong>{' '}
            Todos los elementos de configuración han sido borrados. Las empresas y usuarios se
            mantienen intactos.
          </div>
          <button
            type="button"
            className="btn-close ms-auto"
            onClick={() => setNukeSuccess(false)}
          />
        </div>
      )}

      {seedResult && (
        <div className="alert alert-success alert-dismissible d-flex align-items-center gap-2 mb-4">
          <span style={{ fontSize: 20 }}>🧪</span>
          <div>
            <strong>Datos sinteticos creados correctamente.</strong>{' '}
            Racks: {seedResult.summary?.racks || 0} · Servidores: {seedResult.summary?.servers || 0} · Switches: {seedResult.summary?.switches || 0} · PDUs: {seedResult.summary?.pdus || 0} · Workstations: {seedResult.summary?.workstations || 0}
          </div>
          <button
            type="button"
            className="btn-close ms-auto"
            onClick={() => setSeedResult(null)}
          />
        </div>
      )}

      {seedError && (
        <div className="alert alert-danger alert-dismissible d-flex align-items-center gap-2 mb-4">
          <span style={{ fontSize: 20 }}>⛔</span>
          <div>
            <strong>Error al poblar datos sinteticos.</strong> {seedError}
          </div>
          <button
            type="button"
            className="btn-close ms-auto"
            onClick={() => setSeedError('')}
          />
        </div>
      )}

      {/* ── Cards grid ── */}
      <div className="row g-4">

        <div className="col-12">
          <div className="card border-primary shadow-sm">
            <div
              className="card-header bg-primary text-white d-flex align-items-center gap-2"
              style={{ fontWeight: 600 }}
            >
              <span style={{ fontSize: 18 }}>🧪</span>
              Datos sinteticos de demo
            </div>
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between gap-4 py-2 flex-wrap">
                <div>
                  <h6 className="fw-semibold mb-1">Poblar entorno ficticio</h6>
                  <p className="text-muted small mb-0">
                    Genera un dataset demo coherente para la empresa actual con ubicaciones,
                    racks, switches, patch panels, PDUs, servidores con hardware y discos,
                    conexiones de red y puestos de trabajo.
                  </p>
                </div>
                <button
                  className="btn btn-outline-primary fw-semibold text-nowrap"
                  style={{ minWidth: 220 }}
                  onClick={handleSeedSynthetic}
                  disabled={!companyId || seedingSynthetic}
                  title={!companyId ? 'Selecciona una empresa primero' : ''}
                >
                  {seedingSynthetic ? 'Poblando...' : '🧪 Poblar datos sinteticos'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Zona peligrosa ── */}
        <div className="col-12">
          <div className="card border-danger shadow-sm">
            <div
              className="card-header bg-danger text-white d-flex align-items-center gap-2"
              style={{ fontWeight: 600 }}
            >
              <span style={{ fontSize: 18 }}>⚠️</span>
              Zona peligrosa — Operaciones destructivas irreversibles
            </div>
            <div className="card-body">

              {/* Nuke row */}
              <div className="d-flex align-items-start justify-content-between gap-4 py-3 border-bottom">
                <div>
                  <h6 className="fw-semibold mb-1">🗑️ Borrar todos los datos de configuración</h6>
                  <p className="text-muted small mb-0">
                    Elimina <strong>todos</strong> los elementos, racks, ubicaciones, switches,
                    servidores, IPAM, VLANs, virtualización, WiFi, routers, firewalls, WANs,
                    documentos, logs de auditoría y naming.
                    <br />
                    <span className="text-success fw-semibold">
                      Se conservan: empresas, usuarios y roles.
                    </span>
                  </p>
                </div>
                <button
                  className="btn btn-outline-danger fw-semibold text-nowrap"
                  style={{ minWidth: 160 }}
                  onClick={openNukeModal}
                  disabled={!companyId}
                  title={!companyId ? 'Selecciona una empresa primero' : ''}
                >
                  🗑️ Borrar todo
                </button>
              </div>

              {/* Placeholder for future destructive ops */}
              <div className="d-flex align-items-start justify-content-between gap-4 py-3 opacity-50">
                <div>
                  <h6 className="fw-semibold mb-1 text-muted">🔄 Resetear numeración (próximamente)</h6>
                  <p className="text-muted small mb-0">
                    Reinicia los contadores de naming a cero sin borrar otros datos.
                  </p>
                </div>
                <button className="btn btn-outline-secondary fw-semibold text-nowrap" disabled style={{ minWidth: 160 }}>
                  Próximamente
                </button>
              </div>

            </div>
          </div>
        </div>

        {/* ── Info card ── */}
        <div className="col-12">
          <div className="card border-0 bg-light">
            <div className="card-body py-3">
              <div className="d-flex gap-2 align-items-start">
                <span style={{ fontSize: 20, marginTop: 2 }}>ℹ️</span>
                <div className="small text-muted">
                  <strong className="text-dark">¿Cuándo usar Setup?</strong>
                  <br />
                  Utiliza esta sección para hacer una limpieza completa del entorno antes de
                  cargar datos reales (p.ej. después de una demo o pruebas), o para resetear
                  una empresa a su estado inicial sin necesidad de reprovisionar usuarios.
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── Nuke confirmation modal ── */}
      <Modal show={showNukeModal} onHide={() => !nuking && setShowNukeModal(false)} centered>
        <Modal.Header closeButton={!nuking} className="bg-danger text-white">
          <Modal.Title>
            <span style={{ marginRight: 8 }}>⚠️</span>
            Confirmar borrado total
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning d-flex gap-2 align-items-start">
            <span style={{ fontSize: 22 }}>🚨</span>
            <div>
              Esta operación es <strong>irreversible</strong>. Se eliminarán{' '}
              <strong>todos los datos de configuración</strong> de la empresa actual:
              racks, switches, servidores, IPAM, VLANs, virtualización, WiFi, WAN,
              documentos y logs de auditoría.
              <br /><br />
              <span className="text-success fw-semibold">✔ Se conservan: empresa, usuarios y roles.</span>
            </div>
          </div>

          <Form.Group className="mt-3">
            <Form.Label className="fw-semibold">
              Para confirmar, escribe <code className="text-danger fs-6">BORRAR</code>:
            </Form.Label>
            <Form.Control
              type="text"
              value={nukeConfirm}
              onChange={e => setNukeConfirm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && nukeConfirm === 'BORRAR') handleNuke(); }}
              placeholder="Escribe BORRAR para confirmar"
              className={nukeConfirm && nukeConfirm !== 'BORRAR' ? 'border-danger' : ''}
              autoFocus
              disabled={nuking}
            />
          </Form.Group>

          {nukeError && (
            <div className="alert alert-danger mt-3 mb-0 py-2">
              <strong>Error:</strong> {nukeError}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowNukeModal(false)}
            disabled={nuking}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleNuke}
            disabled={nuking || nukeConfirm !== 'BORRAR'}
          >
            {nuking ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Borrando…
              </>
            ) : (
              '🗑️ Confirmar borrado total'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SetupManager;
